/**
 * Revo Quality Assurance (PH) role in recruitment. Client-safe: no DB access.
 *
 * Revo runs its own QA team of Philippines hires on the existing position
 * "Quality Assurance (PH)" (two employees since the Aug 24 2026 PH cohort; two
 * more are planned with the Oct 19 2026 PH class). A candidate is a Revo QA
 * candidate when the position they applied for is a Philippines Quality
 * Assurance position. For those candidates HR creates the employee straight on
 * the QA (PH) position under the Revo company: they do not join a Ucallz or
 * Teli training class (Revo gives them its own training), so there is no
 * trainer enrollment step and no NESTING queue (Jesus, 2026-10-13).
 *
 * Revo QA candidates are interviewed by the Revo lead, Mario Corona Aguilar
 * (Project Coordinator): he is offered first and preselected in the Stage 1
 * picker for those candidates, on top of the HR interviewers, the scheduling
 * route accepts him for Revo QA positions only, and the scoped Recruitment
 * permission (`client_care_interviewer`) also covers the Revo QA candidates
 * scheduled with him (Jesus, 2026-10-14: "when they are assigned to Revo QA PH
 * position, then its Mario interviewing them").
 */
import { isPhilippinesPositionName } from '@/lib/philippines-payroll';
import {
  employeeMatchesInterviewerEntries,
  nameMatchesInterviewerEntries,
} from '@/lib/recruitment/interviewer-names';

export const REVO_QA_PH_POSITION = 'Quality Assurance (PH)';

/** Role label shown next to the position in the Philippines pick lists. */
export const REVO_QA_ROLE = 'Revo QA';

/** Option for the Philippines application position lists (Accept modal, Recruit form). */
export const REVO_QA_PH_APPLICATION_POSITION = { value: REVO_QA_PH_POSITION, role: REVO_QA_ROLE } as const;

export function isQualityAssurancePositionName(name: string | null | undefined): boolean {
  return /quality\s*assurance|\bqa\b/i.test(name ?? '');
}

/** A Philippines Quality Assurance position = the Revo QA (PH) position. */
export function isRevoQaPositionName(name: string | null | undefined): boolean {
  return isQualityAssurancePositionName(name) && isPhilippinesPositionName(name);
}

/**
 * Postgres regex (case-insensitive match with ~*) equivalent of
 * isRevoQaPositionName: a Quality Assurance / QA position marked "(PH)" or
 * "Philippines", in either order.
 */
export const REVO_QA_POSITION_SQL_PATTERN =
  '(quality\\s*assurance|\\mqa\\M).*(\\(\\s*ph\\s*\\)|philippines)|(\\(\\s*ph\\s*\\)|philippines).*(quality\\s*assurance|\\mqa\\M)';

export function isRevoQaCandidate(
  candidate: { position_applied?: string | null } | null | undefined,
): boolean {
  return isRevoQaPositionName(candidate?.position_applied);
}

/**
 * Who interviews Revo QA candidates, as "First name + first surname"
 * (interviewer-names.ts rules). Mario Corona Aguilar, employee 27.
 */
export const REVO_QA_INTERVIEWERS = ['Mario Corona'] as const;

/** Employee record (first_name / last_name) is a Revo QA interviewer. */
export function isRevoQaInterviewerEmployee(employee: {
  first_name?: string | null;
  last_name?: string | null;
}): boolean {
  return employeeMatchesInterviewerEntries(REVO_QA_INTERVIEWERS, employee);
}

/** Full interviewer name as stored on the interview ("Mario Corona Aguilar"). */
export function isRevoQaInterviewerName(name: string | null | undefined): boolean {
  return nameMatchesInterviewerEntries(REVO_QA_INTERVIEWERS, name);
}

/**
 * Revo QA positions a Revo QA candidate can be hired on (Create Employee): the
 * Philippines Quality Assurance positions, the one they applied for first.
 * Anyone else (agents, Client Care, a Mexico QA position) gets none.
 */
export function revoQaHirePositions<T extends { name: string }>(
  positions: T[],
  candidate: { position_applied?: string | null } | null | undefined,
): T[] {
  if (!isRevoQaCandidate(candidate)) return [];
  const applied = candidate?.position_applied?.trim().toLowerCase() ?? '';
  const isApplied = (p: T) => (p.name.trim().toLowerCase() === applied ? 1 : 0);
  return positions
    .filter((p) => isRevoQaPositionName(p.name))
    .sort((a, b) => isApplied(b) - isApplied(a) || a.name.localeCompare(b.name));
}
